'use client';

import { useState } from 'react';
import { PieChart, Pie, Sector, ResponsiveContainer } from 'recharts';
import { SentimentCount } from '../types';
import { getSentimentColorValue } from '../utils/styleUtils';

interface SentimentPieChartProps {
  sentimentData: SentimentCount[];
  onSentimentClick: (sentiment: string) => void;
}

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 5) * cos;
  const sy = cy + (outerRadius + 5) * sin;
  const mx = cx + (outerRadius + 15) * cos;
  const my = cy + (outerRadius + 15) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 15;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';
  
  // Get the sentiment text
  const sentimentText = payload.sentiment;

  return (
    <g>
      {/* Center text with tspan for text wrapping if needed */}
      <text x={cx} y={cy - 5} textAnchor="middle" fill={fill} fontSize="11">
        {sentimentText.length > 12 ? (
          <>
            <tspan x={cx} dy="0">{sentimentText.substring(0, sentimentText.indexOf(' ') > -1 ? sentimentText.indexOf(' ') : sentimentText.length)}</tspan>
            <tspan x={cx} dy="12">{sentimentText.indexOf(' ') > -1 ? sentimentText.substring(sentimentText.indexOf(' ') + 1) : ''}</tspan>
          </>
        ) : (
          sentimentText
        )}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 3}
        outerRadius={outerRadius + 5}
        fill={fill}
      />
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text x={ex + (cos >= 0 ? 1 : -1) * 10} y={ey} textAnchor={textAnchor} fill="#333" fontSize="10">{`Count ${value}`}</text>
      <text x={ex + (cos >= 0 ? 1 : -1) * 10} y={ey} dy={12} textAnchor={textAnchor} fill="#999" fontSize="8">
        {`(${(percent * 100).toFixed(0)}%)`}
      </text>
    </g>
  );
};

export const SentimentPieChart = ({ sentimentData, onSentimentClick }: SentimentPieChartProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Add check for undefined or null data
  if (!sentimentData || !Array.isArray(sentimentData)) {
      return (
        <div className="flex h-full flex-col items-center justify-center p-4">
          <div className="text-center text-gray-500">No data available</div>
        </div>
      );
  }
  
  // Calculate total for percentages
  const total = sentimentData.reduce((sum, item) => sum + (item.count || 0), 0); // Added fallback for item.count
  
  if (total === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <div className="text-center text-gray-500">No data available</div>
      </div>
    );
  }
  
  // Transform the data for recharts
  // Filter out items with zero count to prevent rendering issues
  const data = sentimentData
    .filter(item => item.count > 0)
    .map(item => ({
      sentiment: item.sentiment,
      value: item.count,
      fill: getSentimentColorValue(item.sentiment)
    }));

  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  return (
    <div className="relative h-full w-full">
      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={42}
              outerRadius={66}
              dataKey="value"
              onMouseEnter={onPieEnter}
              onClick={(data) => onSentimentClick(data.sentiment)}
              paddingAngle={1}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}; 